import { useMemo } from 'react';

import type { AxisOptionGroup } from 'components/molecules/PackArchitecture/AxisConfigDropdown';
import {
    buildPackArchitectureTableTitle,
    getAxisLabel,
    getAxisLabelWithSuffix,
} from 'components/pages/PackArchitecture/packArchitectureMeta.helpers';
import type { PpaMetaControlNode } from 'components/pages/PackArchitecture/packArchitectureMeta.types';

import { formatTableKpiCheckboxLabel, type KpiOption } from './packArchitectureTable.helpers';

type UsePackArchitectureTableMetaParams = {
    xAxis: string;
    xAxisOptionGroups: AxisOptionGroup[];
    yAxis: string;
    yAxisOptionGroups: AxisOptionGroup[];
    bubbleRepresents: string;
    bubbleRepresentsOptionGroups: AxisOptionGroup[];
    tableKpiOptionItems: PpaMetaControlNode[];
};

export function usePackArchitectureTableMeta({
    xAxis,
    xAxisOptionGroups,
    yAxis,
    yAxisOptionGroups,
    bubbleRepresents,
    bubbleRepresentsOptionGroups,
    tableKpiOptionItems,
}: UsePackArchitectureTableMetaParams) {
    const tableKpiOptions: KpiOption[] = useMemo(
        () =>
            tableKpiOptionItems
                .filter((item) => item.id)
                .map((item) => ({
                    id: item.id!,
                    label: item.label ?? item.id ?? '',
                })),
        [tableKpiOptionItems],
    );

    const { yAxisLabel, tableTitle } = useMemo(() => {
        const xLabel = getAxisLabelWithSuffix(xAxis, xAxisOptionGroups);
        const yLabel = getAxisLabelWithSuffix(yAxis, yAxisOptionGroups) || 'Y-Axis';
        const bubbleLabel = getAxisLabel(bubbleRepresents, bubbleRepresentsOptionGroups);
        return {
            yAxisLabel: yLabel,
            tableTitle: buildPackArchitectureTableTitle(xLabel, bubbleLabel),
        };
    }, [xAxis, xAxisOptionGroups, yAxis, yAxisOptionGroups, bubbleRepresents, bubbleRepresentsOptionGroups]);

    const kpiOptions: KpiOption[] = useMemo(
        () => [
            {
                id: yAxis,
                label: formatTableKpiCheckboxLabel(yAxisLabel),
            },
            ...tableKpiOptions,
        ],
        [yAxis, yAxisLabel, tableKpiOptions],
    );

    return { tableKpiOptions, tableTitle, kpiOptions };
}
